/**
 * Venue operating hours: parsing, display, and open-now logic.
 *
 * Hours are stored as strings in two historical shapes — 24-hour "17:00" (web
 * admin form) and 12-hour "5:00 PM" (Google Places importer). Everything here
 * reads both; new writes should go through `normalizeVenueTime` so the stored
 * shape converges on 24-hour. Hours are wall-clock times in the venue's own
 * IANA timezone (`Venue.timezone`), never the viewer's.
 */
const WEEKDAYS = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
];
const TIME_PATTERN = /^(\d{1,2})(?::(\d{2}))?\s*([AP])\.?M\.?$|^(\d{1,2}):(\d{2})$/i;
/**
 * Minutes since midnight for "17:00" or "5:00 PM", or null when unreadable.
 *
 * @example parseVenueTime('5:00 PM') // 1020
 */
export const parseVenueTime = (value) => {
    if (!value)
        return null;
    const m = TIME_PATTERN.exec(value.trim());
    if (!m)
        return null;
    if (m[3]) {
        const hour = parseInt(m[1], 10);
        const minute = parseInt(m[2] ?? '0', 10);
        if (hour < 1 || hour > 12 || minute > 59)
            return null;
        return ((hour % 12) + (m[3].toUpperCase() === 'P' ? 12 : 0)) * 60 + minute;
    }
    const hour = parseInt(m[4], 10);
    const minute = parseInt(m[5], 10);
    if (hour > 23 || minute > 59)
        return null;
    return hour * 60 + minute;
};
/** Zero-padded 24-hour "HH:MM" for either stored shape, or null when unreadable. */
export const normalizeVenueTime = (value) => {
    const minutes = parseVenueTime(value);
    if (minutes === null)
        return null;
    const hh = String(Math.floor(minutes / 60)).padStart(2, '0');
    const mm = String(minutes % 60).padStart(2, '0');
    return `${hh}:${mm}`;
};
/**
 * A copy of an hours map with every open/close time rewritten as 24-hour
 * "HH:MM". Closed days and text that isn't a time are kept as they are.
 */
export const normalizeVenueHours = (hours) => {
    const out = {};
    for (const [day, entry] of Object.entries(hours)) {
        out[day] =
            !entry || entry.closed
                ? entry
                : {
                    ...entry,
                    open: normalizeVenueTime(entry.open) ?? entry.open,
                    close: normalizeVenueTime(entry.close) ?? entry.close,
                };
    }
    return out;
};
/** "5:00 PM" for either stored shape; text that isn't a time is returned unchanged. */
export const formatVenueTime = (value) => {
    const minutes = parseVenueTime(value);
    if (minutes === null)
        return value ?? '';
    const hour = Math.floor(minutes / 60);
    const minute = String(minutes % 60).padStart(2, '0');
    return `${hour % 12 === 0 ? 12 : hour % 12}:${minute} ${hour >= 12 ? 'PM' : 'AM'}`;
};
/**
 * The weekday and wall-clock minutes at the venue right now. Without a
 * timezone (venue not yet backfilled) this falls back to the device clock,
 * which is only right when the viewer is standing at the venue's timezone.
 */
export const venueZonedNow = (now, timezone) => {
    if (timezone) {
        try {
            const parts = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone,
                weekday: 'long',
                hour: 'numeric',
                minute: 'numeric',
                hourCycle: 'h23',
            }).formatToParts(now);
            const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
            const weekday = get('weekday').toLowerCase();
            if (WEEKDAYS.includes(weekday)) {
                // Some engines render midnight as "24" even under h23.
                const hour = parseInt(get('hour'), 10) % 24;
                return { weekday, minutes: hour * 60 + parseInt(get('minute'), 10) };
            }
        }
        catch {
            // Unknown timezone name — fall through to the device clock.
        }
    }
    return { weekday: WEEKDAYS[now.getDay()], minutes: now.getHours() * 60 + now.getMinutes() };
};
/**
 * True / false when the venue's hours are known, null when it has none at all
 * (so callers can hide the badge instead of claiming "Closed").
 */
export const isVenueOpenNow = (hours, timezone, now = new Date()) => {
    if (!hours)
        return null;
    const { weekday, minutes } = venueZonedNow(now, timezone);
    const today = hours[weekday];
    if (today && !today.closed) {
        const open = parseVenueTime(today.open);
        const close = parseVenueTime(today.close);
        if (open !== null && close !== null) {
            // A close at or before the open runs past midnight; only the evening half is today's.
            if (close <= open ? minutes >= open : minutes >= open && minutes <= close)
                return true;
        }
    }
    // The small hours of today may still belong to yesterday's late close.
    const yesterday = hours[WEEKDAYS[(WEEKDAYS.indexOf(weekday) + 6) % 7]];
    if (yesterday && !yesterday.closed) {
        const open = parseVenueTime(yesterday.open);
        const close = parseVenueTime(yesterday.close);
        if (open !== null && close !== null && close <= open && minutes <= close)
            return true;
    }
    return false;
};
/** True when `timezone` is an IANA zone name this runtime can resolve. */
export const isValidTimeZone = (timezone) => {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: timezone });
        return true;
    }
    catch {
        return false;
    }
};
/** Short zone name for display ("PDT"); empty for a missing or invalid zone. */
export const venueZoneAbbreviation = (timezone, at = new Date()) => {
    if (!timezone)
        return '';
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            timeZoneName: 'short',
        }).formatToParts(at);
        return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
    }
    catch {
        return '';
    }
};
